import { JWTRole } from "@hackthenorth/north";
import React, { createContext, useContext } from "react";

import {
  useEventContext,
  useUserContext,
  useSponsorContext,
  useHackerContext,
} from "src/shared/contexts";
import { canEditRsvp, hasRsvpAccess } from "src/shared/utils/attendeeAccess";
import { IS_PRODUCTION } from "src/shared/utils/env";
import { useSponsorPerkClaim } from "src/views/sponsor/perks/SponsorPerksContext/useSponsorPerkClaim";
import { SponsorPerkType } from "src/views/sponsor/perks/types";

import { HackerStage } from "../HackerContext/types";

import { Permission } from "./types";
import {
  hasAttendeeEventAccess,
  hasMentorEventAccess,
  hasSponsorEventAccess,
  hasWorkshopLeadEventAccess,
} from "./utils";

export interface PermissionsContextState {
  isLoading: boolean;
  permissions: Permission[];
  hasPermission: (permission: Permission) => boolean;
  hasPermissions: (permissions?: Permission[], requireAll?: boolean) => boolean;
}

const DEFAULT_STATE: PermissionsContextState = {
  isLoading: true,
  permissions: [],
  hasPermission: () => {
    throw new Error("No provider found");
  },
  hasPermissions: () => {
    throw new Error("No provider found");
  },
};

const PermissionsContext: React.Context<PermissionsContextState> =
  createContext(DEFAULT_STATE);

export const usePermissionsContext = () => useContext(PermissionsContext);

export const PermissionsContextProvider: React.FC = ({ children }) => {
  const { roles, isAuthenticated, isOrganizer, attendeeFound } =
    useUserContext();
  const { loading: isSponsorLoading } = useSponsorContext();
  const { stage, claimFound, isLoading: isHackerLoading } = useHackerContext();
  const { perks } = useSponsorPerkClaim();
  const { rsvp, isLoading: isEventLoading } = useEventContext();
  const hasRsvpRole = hasRsvpAccess(roles, isOrganizer);
  // A current-event role and RSVP claim authorize access. The separate
  // attendee lookup must not block an existing RSVP (its event count can differ).
  const hasConfirmedRsvp =
    hasRsvpRole && hasAttendeeEventAccess(claimFound, stage);

  const hasPermission: PermissionsContextState["hasPermission"] = (
    permission: Permission
  ) => {
    if (permission === Permission.RSVP_FORM) return rsvp.isOpen;
    if (permission === Permission.TRAVEL_PLAN_FORM)
      return rsvp.isTravelPlanOpen;
    if (permission === Permission.RSVP_EDIT)
      return hasRsvpRole && claimFound === true && canEditRsvp(stage);
    if (isOrganizer && permission !== Permission.TICKET_ACCESS) return true;

    // values that are shared across multiple permissions
    const isSponsor = roles.includes(JWTRole.SPONSOR);
    const isHacker = roles.includes(JWTRole.HACKER);
    const isVolunteer = roles.includes(JWTRole.VOLUNTEER);
    const isMentor = roles.includes(JWTRole.MENTOR);
    const isWorkshopLead = roles.includes(JWTRole.WORKSHOP_LEAD);
    const isJudge = roles.includes(JWTRole.JUDGE);
    const isGeneral = roles.includes("general" as JWTRole);

    switch (permission) {
      case Permission.SPONSOR:
        return isSponsor;

      case Permission.HACKER:
        return isHacker && !IS_PRODUCTION;

      case Permission.VOLUNTEER:
        return isVolunteer;

      case Permission.MENTOR:
        return isMentor && !IS_PRODUCTION;

      case Permission.WORKSHOP_LEAD:
        return isWorkshopLead && !IS_PRODUCTION;

      case Permission.GENERAL:
        return isGeneral;

      case Permission.SPONSOR_RECRUITMENT_ACCESS:
        return isSponsor;

      case Permission.SPONSOR_SHIPPED_SWAG_ACCESS:
        return (
          isSponsor &&
          (perks?.available_perks?.value?.includes(
            SponsorPerkType.SHIPPED_SWAG
          ) ??
            false)
        );

      case Permission.HACKER_RSVP:
        return (
          isHacker &&
          (stage === HackerStage.ACCEPTED ||
            stage === HackerStage.AUTO_ACCEPTED ||
            stage === HackerStage.WAITLISTED ||
            stage === HackerStage.WITHDRAWN ||
            stage === HackerStage.CONFIRMED ||
            stage === HackerStage.EXPIRED ||
            stage === HackerStage.NOT_ACCEPTED)
        );

      case Permission.HACKER_RSVP_NOT_RESTRICTED:
        return (
          isHacker &&
          (stage === HackerStage.ACCEPTED ||
            stage === HackerStage.AUTO_ACCEPTED ||
            stage === HackerStage.CONFIRMED)
        );

      case Permission.RSVP:
        return hasRsvpRole;

      case Permission.TICKET_ACCESS:
        // Confirmed and checked-in attendees may view their ticket; registration
        // staff manually verify waiver completion during check-in.
        return hasConfirmedRsvp;

      case Permission.ATTENDEE_EVENT_ACCESS:
        return hasConfirmedRsvp;

      case Permission.HACKER_EVENT_ACCESS:
        return (
          isHacker &&
          (stage === HackerStage.CONFIRMED || stage === HackerStage.CHECKED_IN)
        );

      case Permission.MENTOR_EVENT_ACCESS:
        return hasMentorEventAccess(isMentor, stage);

      case Permission.SPONSOR_EVENT_ACCESS:
        return hasSponsorEventAccess(isSponsor, stage);

      case Permission.WORKSHOP_LEAD_EVENT_ACCESS:
        return hasWorkshopLeadEventAccess(isWorkshopLead, stage);

      case Permission.VOLUNTEER_EVENT_ACCESS:
        return (
          isVolunteer &&
          (stage === HackerStage.CONFIRMED || stage === HackerStage.CHECKED_IN)
        );

      case Permission.VIEW_FULL_SCHEDULE:
        return (
          isSponsor || hasConfirmedRsvp || isMentor || isWorkshopLead || isJudge
        );

      case Permission.STAGING:
        return !IS_PRODUCTION;

      case Permission.ORGANIZER:
        return isOrganizer;

      case Permission.JUDGE:
        return isJudge && !IS_PRODUCTION;

      default:
        throw new Error(`Invalid permission found: ${permission}`);
    }
  };

  const hasPermissions: PermissionsContextState["hasPermissions"] = (
    permissions: Permission[] = [],
    requireAll = true
  ) =>
    requireAll
      ? permissions.every((p) => hasPermission(p))
      : permissions.some((p) => hasPermission(p));

  const permissions: PermissionsContextState["permissions"] = Object.values(
    Permission
  ).filter((p) => hasPermission(p));

  const isLoading =
    isEventLoading ||
    (isAuthenticated && attendeeFound === null) ||
    (isSponsorLoading && hasPermission(Permission.SPONSOR)) ||
    (isHackerLoading && hasPermission(Permission.RSVP));

  return (
    <PermissionsContext.Provider
      value={{ isLoading, permissions, hasPermission, hasPermissions }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};
